import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { UploadedFile } from 'components/shareable/UniversalUploadWidget/UniversalUploadWidget'
import Axios from 'helpers/Interceptor'
import { toastError, toastSuccess } from 'modules/Toast'
import useBrand from './useBrand'

// Store file input ref outside of immer state to avoid DOM element issues
let globalFileInputRef: HTMLInputElement | null = null

interface UniversalUploadWidgetState {
    // State
    uploadedAssets: UploadedFile[]
    uploadingFiles: UploadedFile[]
    isDragOver: boolean
    isUploading: boolean
    supportedFileTypes: string[]

    // Actions for uploaded assets (completed uploads)
    setUploadedAssets: (files: UploadedFile[]) => void
    addUploadedAsset: (file: UploadedFile) => void
    removeUploadedAsset: (fileId: string) => void
    clearUploadedAssets: () => void

    // Actions for uploading files (in-progress uploads)
    setUploadingFiles: (files: UploadedFile[]) => void
    updateUploadingFile: (fileId: string, updates: Partial<UploadedFile>) => void
    removeUploadingFile: (fileId: string) => void
    clearUploadingFiles: () => void

    // UI state
    setIsDragOver: (isDragOver: boolean) => void
    setIsUploading: (isUploading: boolean) => void

    // Upload lifecycle handlers
    handleUploadStart: (files: UploadedFile[]) => void
    handleUploadProgress: (fileId: string, progress: number) => void
    handleUploadComplete: (file: UploadedFile) => void
    handleUploadError: (fileId: string, error: string) => void
    handleUpload: (files: UploadedFile[]) => void
    handlePreviewClick?: (file: UploadedFile) => void

    // Upload functions
    uploadToS3: (file: UploadedFile, chatId?: string, skipAgentNotification?: boolean) => Promise<void>
    uploadFiles: (files: UploadedFile[], chatId?: string, skipAgentNotification?: boolean) => Promise<void>
    retryUpload: (fileId: string, chatId?: string, skipAgentNotification?: boolean) => Promise<void>

    // Widget functions
    setFileInputRef: (ref: HTMLInputElement | null) => void
    openUniversalUploadWidget: (supportedFileTypes: string[]) => void

    // Universal remove method that works for both uploaded and uploading files
    removeFile: (fileId: string) => void

    // Utility methods
    hasUploadedAssets: () => boolean
    hasUploadingFiles: () => boolean
    setSupportedFileTypes: (types: string[]) => void
    getAllUploadedAssetIds: () => number[]
    reset: () => void // Clear everything
}

const useUniversalUploadWidget = create(immer<UniversalUploadWidgetState>((set, get) => ({
    // Initial state
    uploadedAssets: [],
    uploadingFiles: [],
    isDragOver: false,
    isUploading: false,
    supportedFileTypes: [
        // Images
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/webp',
        // Videos
        'video/mp4',
        'video/quicktime',
        'video/avi',
        'video/mov',
        // Documents
        'text/csv',
        'application/pdf',
        'application/json',
        'text/plain',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        // Audio
        'audio/mpeg',
        'audio/wav',
        'audio/ogg'
    ],

    setSupportedFileTypes: (types) => {
        set((state) => {
            state.supportedFileTypes = types
        })
    },

    // Uploaded assets actions
    setUploadedAssets: (files) => {
        set((state) => {
            state.uploadedAssets = files
        })
    },

    addUploadedAsset: (file) => {
        set((state) => {
            // Only add if not already present
            const exists = state.uploadedAssets.some(f => f.id === file.id)
            if (!exists) {
                state.uploadedAssets.push(file)
            }
        })
    },

    removeUploadedAsset: (fileId) => {
        set((state) => {
            state.uploadedAssets = state.uploadedAssets.filter(f => f.id !== fileId)
        })
    },

    clearUploadedAssets: () => {
        set((state) => {
            state.uploadedAssets = []
        })
    },

    // Uploading files actions
    setUploadingFiles: (files) => {
        set((state) => {
            state.uploadingFiles = files
            state.isUploading = files.length > 0
        })
    },

    updateUploadingFile: (fileId, updates) => {
        set((state) => {
            const fileIndex = state.uploadingFiles.findIndex(f => f.id === fileId)
            if (fileIndex !== -1) {
                state.uploadingFiles[fileIndex] = { ...state.uploadingFiles[fileIndex], ...updates }
            }
        })
    },

    removeUploadingFile: (fileId) => {
        set((state) => {
            state.uploadingFiles = state.uploadingFiles.filter(f => f.id !== fileId)
            state.isUploading = state.uploadingFiles.length > 0
        })
    },

    clearUploadingFiles: () => {
        set((state) => {
            state.uploadingFiles = []
            state.isUploading = false
        })
    },

    // UI state actions
    setIsDragOver: (isDragOver) => {
        set((state) => {
            state.isDragOver = isDragOver
        })
    },

    setIsUploading: (isUploading) => {
        set((state) => {
            state.isUploading = isUploading
        })
    },

    // Upload lifecycle handlers
    handleUploadStart: (files) => {
        console.log('Upload started for files:', files.map(f => f.name))
        get().setUploadingFiles(files)
    },

    handleUploadProgress: (fileId, progress) => {
        get().updateUploadingFile(fileId, { uploadProgress: progress })
    },

    handleUploadComplete: (file) => {
        console.log('Upload completed for file:', file.name)
        // Move from uploading to uploaded
        get().removeUploadingFile(file.id)
        get().addUploadedAsset(file)
    },

    handleUploadError: (fileId, error) => {
        console.error('Upload error for file:', fileId, error)
        get().updateUploadingFile(fileId, {
            status: 'error',
            errorMessage: error
        })
    },

    handleUpload: (files) => {
        console.log('Upload finished, completed files:', files.map(f => f.name))
        // This is called when all uploads in a batch are complete
        // Clear uploading files and set uploaded assets
        get().clearUploadingFiles()
        get().setUploadedAssets(files)
    },

    handlePreviewClick: undefined, // Can be set by components that need it

    // Upload functions
    uploadToS3: async (file, chatId, skipAgentNotification = false) => {
        const formData = new FormData()
        if (file.file) formData.append('files', file.file)
        if (chatId) formData.append('chatId', chatId)
        formData.append('generateThumbnails', 'true')
        if (skipAgentNotification) formData.append('skipAgentNotification', 'true')

        const response = await Axios.post(
            `/uploaded-assets/smart-upload`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1))
                    get().handleUploadProgress(file.id, progress)
                }
            }
        )

        if (!response.data.isError && response.data.uploads.length > 0) {
            const uploadResult = response.data.uploads[0]

            get().updateUploadingFile(file.id, {
                status: 'ready',
                uploadProgress: 100,
                uploadedAssetId: uploadResult.id,
                s3Url: uploadResult.url
            })

            const updatedFile: UploadedFile = {
                ...file,
                status: 'ready',
                uploadProgress: 100,
                uploadedAssetId: uploadResult.id,
                s3Url: uploadResult.url
            }

            get().handleUploadComplete(updatedFile)
        } else {
            throw new Error(response.data.message || 'S3 upload failed')
        }
    },

    uploadFiles: async (files, chatId, skipAgentNotification = false) => {
        const uploadResults: UploadedFile[] = []

        for (const file of files) {
            try {
                get().updateUploadingFile(file.id, { status: 'uploading' })
                await get().uploadToS3(file, chatId, skipAgentNotification)

                // Get the uploaded file from uploadedAssets (where it was moved to)
                const updatedFile = get().uploadedAssets.find(f => f.id === file.id)
                if (updatedFile && updatedFile.status === 'ready') {
                    uploadResults.push(updatedFile)
                }

            } catch (error) {
                console.error(`Error uploading ${file.name}:`, error)
                const errorMessage = error instanceof Error ? error.message : 'Upload failed'

                get().updateUploadingFile(file.id, { status: 'error', errorMessage })
                toastError(`Failed to upload ${file.name}: ${errorMessage}`)
                get().handleUploadError(file.id, errorMessage)
            }
        }

        // Show success message if any files were uploaded successfully
        if (uploadResults.length > 0) {
            toastSuccess('Files uploaded successfully')
        }
    },

    retryUpload: async (fileId, chatId, skipAgentNotification = false) => {
        const fileToRetry = get().uploadingFiles.find(f => f.id === fileId)

        if (!fileToRetry) return

        try {
            get().updateUploadingFile(fileId, {
                status: 'uploading',
                uploadProgress: 0,
                retryCount: (fileToRetry.retryCount || 0) + 1,
                errorMessage: undefined
            })

            await get().uploadToS3(fileToRetry, chatId, skipAgentNotification)

        } catch (error) {
            console.error(`Error retrying upload for ${fileToRetry.name}:`, error)
            const errorMessage = error instanceof Error ? error.message : 'Upload failed'
            const currentRetryCount = (fileToRetry.retryCount || 0) + 1

            get().updateUploadingFile(fileId, {
                status: 'error',
                errorMessage,
                retryCount: currentRetryCount
            })

            toastError(`Failed to upload ${fileToRetry.name}: ${errorMessage}`)
            get().handleUploadError(fileId, errorMessage)
        }
    },

    // Widget functions
    setFileInputRef: (ref) => {
        globalFileInputRef = ref
    },

    openUniversalUploadWidget: async (supportedFileTypes: string[]) => {
        // Ensure brand is available before resuming chat
        const { currentBrand: primaryBrand, ensureBrandAvailable } = useBrand.getState()

        let brandId = primaryBrand?.id;
        if (!brandId) {
            const newBrand = await ensureBrandAvailable();
            brandId = newBrand?.id;
        }

        get().setSupportedFileTypes(supportedFileTypes)
        if (globalFileInputRef) {
            // Small timeout to allow component to re-render with new supportedFileTypes
            setTimeout(() => {
                globalFileInputRef?.click()
            }, 0)
        }
    },

    // Universal remove method that works for both uploaded and uploading files
    removeFile: (fileId) => {
        get().removeUploadedAsset(fileId)
        get().removeUploadingFile(fileId)
    },

    // Utility methods
    hasUploadedAssets: () => {
        return get().uploadedAssets.length > 0
    },

    hasUploadingFiles: () => {
        return get().uploadingFiles.length > 0
    },

    getAllUploadedAssetIds: () => {
        return get().uploadedAssets
            .filter(file => file.uploadedAssetId)
            .map(file => file.uploadedAssetId!)
    },

    reset: () => {
        set((state) => {
            state.uploadedAssets = []
            state.uploadingFiles = []
            state.isDragOver = false
            state.isUploading = false
        })
    }
})))

export default useUniversalUploadWidget 